<template>
    <div class="col-lg-8 mx-auto">

        <SubSection v-if="showNotifications" title="Messages" :show-help="false">
            <div class="d-grid row-gap-4 mb-2" role="list">
                <Notification v-for="notification, i in notifications" :key="i" :notification="notification"></Notification>
            </div>
        </SubSection>

        <SubSection v-if="viewModel.canShowDashboardOption" :title="viewModel.dashboardTitle" :show-help="false">
            <p>
                {{ viewModel.dashboardText }}
            </p>

            <div v-if="viewModel.dashboardAccessWarning" class="alert alert-primary mt-2" role="alert">
                <i class="bi bi-exclamation-circle-fill"></i>
                {{ viewModel.dashboardAccessWarning }}
            </div>

            <button class="btn btn-primary" @click="onDashboardClicked">Dashboard</button>
        </SubSection>

        <SubSection :title="viewModel.mainJourneyReferenceText" :show-help="false">
            <template v-if="customContentKey != UserHomeCustomContentKey.None">
                <AonUserHome v-if="customContentKey == UserHomeCustomContentKey.Aon"></AonUserHome>
            </template>

            <p v-for="(introText, i) in viewModel.mainJourneyText" :key="i" :class="{ 'mt-0': i === 0 }">{{ introText }}</p>

            <template v-if="customContentKey == UserHomeCustomContentKey.Aon">
                <div class="form-check form-switch">
                    <input
                        id="consentToTermsAndConditions"
                        type="checkbox"
                        class="form-check-input"
                        v-model="viewModel.termsAndConditionsConsent"
                        role="switch"
                        @change="handleTermsAndConditionsConsentToggle"
                        :disabled="viewModel.termsAndConditionsConsentToggleDisabled" />
                    <label class="form-check-label ms-2 mb-4 fw-bold" for="consentToTermsAndConditions">
                        Please read and agree to our <a target="_blank" :href="viewModel.termsOfUseResource">terms of use</a> before continuing.
                    </label>
                </div>
            </template>

            <p v-if="viewModel.showAdviceLink">
                <a href="/howtocompletedesign" target="_blank">Find out about building a model of your retirement.</a>
            </p>

            <p class="steps-complete">Steps complete: {{ viewModel.completedSteps }}/{{ viewModel.totalSteps }}</p>

            <dynamic-advice-viewer :message="submissionError" alertContext="danger"></dynamic-advice-viewer>

            <button class="btn btn-primary" @click="onMainJourneyClicked" :disabled="customContentKey == UserHomeCustomContentKey.Aon && viewModel.disableContinue">Continue</button>
        </SubSection>

        <template v-if="customContentKey == UserHomeCustomContentKey.None">
            <p v-if="viewModel.showTermsOfUse"><small>By continuing, you are agreeing to our <a target="_blank" :href="viewModel.termsOfUseResource">terms of use</a>.</small></p>
        </template>
    </div>

    <modal title="Data sharing consent" ref="consentModal" idKey="consentModalKey">
        <consent-capture :info="consentInfo" @finished="onMissingConsentsCaptured"></consent-capture>
    </modal>
</template>
  
<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { app } from "../ts/app";
import { useStore } from "vuex";
import { workflow } from "@/guiide/ts/workflow/workflow";
import { WorkflowStep } from "@/guiide/ts/workflow/workflowStep";
import ConsentInfo, { emptyConsentInfo } from "../ts/consent/consentInfo";
import PermittedEntityType from "../ts/consent/permittedEntityType";
import { Consent } from "../ts/consent";
import { Notification as AppNotification } from "../ts/notifications/notification";
import SubSection from "@/guiide/components/common/SubSection.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import ConsentCapture from "./consent/ConsentCapture.vue";
import Notification from "./common/Notification.vue";
import UserHomeCustomContentKey from "../ts/userhomeCustomContentKey";
import AonUserHome from "./userhome/AonUserHome.vue";
import NotificationConcern from "../ts/notifications/notificationConcern";
import LifeExpectancyBasis from "../ts/lifeExpectancyBasis";
import { hasConsented } from "@/guiide/ts/utilities";
import ConsentType from "@/guiide/ts/consent/consentType";
import ExternalAgency from "@/guiide/ts/consent/externalAgency";
import DynamicAdviceViewer from "@/guiide/components/common/DynamicAdviceViewer.vue";
import Result from "@/guiide/ts/result";
import { useUIContent } from "../ts/configuration/useUIContent";

type ViewModel = {
    canShowDashboardOption: boolean,
    dashboardTitle: string,
    dashboardText: string,
    dashboardAccessWarning: string,
    mainJourneyReferenceText: string,
    mainJourneyText: string[],
    showAdviceLink: boolean,
    completedSteps: string,
    totalSteps: string,
    showTermsOfUse: boolean,
    termsOfUseResource: string
    termsAndConditionsConsentToggleDisabled: boolean,
    termsAndConditionsConsent: boolean,
    disableContinue: boolean
}

const store = useStore();
const { textArray } = useUIContent();
const workflowSteps = ref<Array<WorkflowStep>>(null);
const notifications = ref<Array<AppNotification>>(new Array<AppNotification>());
const consentModal = ref(null);
const modalIsOpen = ref(false);
const consentInfo = ref<ConsentInfo>(emptyConsentInfo);
const dashboardAccessWarning = ref("");
const submissionError = ref("");

onMounted(async () => {
  workflowSteps.value = await workflow.getSteps();
  notifications.value = await app.getNotifications(NotificationConcern.General);
});

watch(
    () => [ store.state.configuration, store.state.retiree ],
    ([config, retiree]) => {
        if (config == null || retiree == null || modalIsOpen.value) {
            return;
        }

        if (retiree.missingConsents.length > 0) {
            const missingConsent: Consent = retiree.missingConsents[0];
            modalIsOpen.value = true;
            consentInfo.value = new ConsentInfo(
                config.companyFullName, 
                config.companyAbbrevName, 
                PermittedEntityType.ExternalAgency,
                missingConsent.permittedEntityId,
                missingConsent.type);
            consentModal.value.showModal();
        }
    }
)

const viewModel = computed<ViewModel>(() => {
    const config = store.state.configuration;
    const retiree = store.state.retiree;

    if (config == null || retiree == null) {
        return getEmptyViewModel();
    }

    const canShowDashboardOption = retiree.isRegistered && !retiree.mustConfirmRetired;

    let showAdviceLink = false;
    let mainJourneyText: string[];
    
    if (!retiree.hasFinishedDesign) {
        mainJourneyText = textArray("userhome.mainjourney.statement", "does-not-have-finished-design");
        showAdviceLink = true;
    } else if (!retiree.isRegistered) {
        mainJourneyText = textArray("userhome.mainjourney.statement", "not-registered");
    } else {
        mainJourneyText = textArray("userhome.mainjourney.statement", "registered");
    }

    const termsAndConditionsConsent = hasConsented(ExternalAgency.Aon, ConsentType.TermsAndConditionsAccepted);

    return {
        canShowDashboardOption,
        dashboardTitle: config.userHomeTrackIntroTitle,
        dashboardText: config.userHomeTrackIntroText,
        dashboardAccessWarning: dashboardAccessWarning.value,
        mainJourneyReferenceText: config.mainJourneyReferenceText,
        mainJourneyText,
        showAdviceLink,
        completedSteps: config.workflow.stepsCompleted.toString(),
        totalSteps: workflowSteps.value?.length.toString() ?? "",
        showTermsOfUse: config.showTermsOfUse,
        termsOfUseResource: config.termsOfUseResource,
        termsAndConditionsConsentToggleDisabled: store.state.transactionOngoing,
        termsAndConditionsConsent: termsAndConditionsConsent,
        disableContinue: !termsAndConditionsConsent
    }
});

const customContentKey = computed<UserHomeCustomContentKey>(() => store.state.configuration?.userHomeCustomContentKey ?? UserHomeCustomContentKey.None);

const showNotifications = computed<boolean>(() => notifications.value.length > 0);

function onDashboardClicked() {
    const config = store.state.configuration;
    const retiree = store.state.retiree;

    if (config == null || retiree == null) {
        return;
    }

    if (config.lifeExpectancyBasis == LifeExpectancyBasis.Survival && retiree.survivalPercentile == null) {
        dashboardAccessWarning.value = "Please enter the main journey to examine any effect of the new method of working out your plan duration.";
        return;
    }

    window.location.href = `/${config.nav.track.resource}`;
}

function onMainJourneyClicked() {
    window.location.href = `/home/startdesign`;
}

function onMissingConsentsCaptured() {
    consentModal.value.closeModal();
    modalIsOpen.value = false;
}

function getEmptyViewModel() {
    return {
        canShowDashboardOption: false,
        dashboardTitle: "",
        dashboardText: "",
        dashboardAccessWarning: "",
        mainJourneyReferenceText: "",
        mainJourneyText: [],
        showAdviceLink: false,
        completedSteps: "",
        totalSteps: "",
        showTermsOfUse: false,
        termsOfUseResource: "",
        termsAndConditionsConsentToggleDisabled: true,
        termsAndConditionsConsent: false,
        disableContinue: true
    };
}

async function handleTermsAndConditionsConsentToggle() {
    viewModel.value.disableContinue = true;

    let consented = viewModel.value.termsAndConditionsConsent;

    let toggleConsentResult: Result;
    if (consented) {
        toggleConsentResult = await app.grantConsent(PermittedEntityType.ExternalAgency, ExternalAgency.Aon, ConsentType.TermsAndConditionsAccepted, true);
    } else {
        toggleConsentResult = await app.revokeConsent(PermittedEntityType.ExternalAgency, ExternalAgency.Aon, ConsentType.TermsAndConditionsAccepted);
    }

    if (toggleConsentResult.successful) {
        viewModel.value.disableContinue = !consented;
    } else {
        submissionError.value = "Failed to save change.";
    }
}

</script>
  